import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table } from "antd";
import SearchBar from "../common/SearchBar";
import CustomButton from "../Buttons/CustomButton";

const TrackChartSelectorModal = ({
  visible,
  onClose,
  onSelect,
  title = "Assign Track to Chart",
  excludeIds = [],
  mode = "default",
  onSearch,
  onLoadPage,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 50;

  // Refs
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const tableBodyRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  
  // Use refs for latest values in scroll handler
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  const currentPageRef = useRef(currentPage);
  const searchTermRef = useRef(searchTerm);

  // Keep refs in sync with state
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Reset when modal opens
  useEffect(() => {
    if (visible) {
      setSearchTerm("");
      setTracks([]);
      setCurrentPage(1);
      setHasMore(true);
      isLoadingMoreRef.current = false;
      loadInitialTracks();
    } else {
      // Cleanup on close
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    }
  }, [visible]);

  // Load initial tracks
  const loadInitialTracks = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    isLoadingMoreRef.current = false;
    
    try {
      const result = await onLoadPage(pageSize, 1);
      
      if (!abortControllerRef.current.signal.aborted) {
        setTracks(result.tracks || []);
        setHasMore(result.hasMore ?? (result.tracks?.length >= pageSize));
        setCurrentPage(1);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to load tracks:", error);
        setTracks([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      isLoadingMoreRef.current = false;
    }
  };

  // Load more tracks (infinite scroll)
  const loadMoreTracks = async () => {
    if (isLoadingMoreRef.current || !hasMoreRef.current || loadingRef.current) {
      return;
    }
    
    isLoadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = currentPageRef.current + 1;

    try {
      const result = searchTermRef.current.trim()
        ? await onSearch(searchTermRef.current, pageSize, nextPage)
        : await onLoadPage(pageSize, nextPage);

      const newTracks = result.tracks || [];
      
      if (newTracks.length > 0) {
        setTracks((prev) => [...prev, ...newTracks]);
        setCurrentPage(nextPage);
        setHasMore(result.hasMore ?? (newTracks.length >= pageSize));
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to load more tracks:", error);
      setHasMore(false);
    } finally {
      isLoadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  // Search tracks
  const performSearch = async (term) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setCurrentPage(1);
    isLoadingMoreRef.current = false;
    
    try {
      const result = term.trim()
        ? await onSearch(term, pageSize, 1)
        : await onLoadPage(pageSize, 1);

      if (!abortControllerRef.current.signal.aborted) {
        setTracks(result.tracks || []);
        setHasMore(result.hasMore ?? (result.tracks?.length >= pageSize));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Search failed:", error);
        setTracks([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      isLoadingMoreRef.current = false;
    }
  };

  // Debounced search
  const handleSearchTermChange = useCallback((newTerm) => {
    setSearchTerm(newTerm);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (newTerm.trim() === "") {
      performSearch("");
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(newTerm);
    }, 500);
  }, []);

  // Scroll handler using refs
  const handleScroll = useCallback((e) => {
    const target = e.target;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    
    if (scrollBottom < 200 && hasMoreRef.current && !loadingRef.current && !isLoadingMoreRef.current) {
      loadMoreTracks();
    }
  }, []);

  // Attach scroll listener
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      const tableBody = document.querySelector('.ant-table-body');
      if (tableBody) {
        tableBodyRef.current = tableBody;
        tableBody.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
          tableBody.removeEventListener('scroll', handleScroll);
        };
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [visible, handleScroll]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSelect = (track) => {
    console.log("Selected track:", track);
    const isAlreadyAssigned = isTrackAssigned(track);
    
    if (isAlreadyAssigned) {
      // Show toast but don't close modal - let user see the message
      return;
    }
    
    onSelect(track);
    setSearchTerm("");
    onClose();
  };

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  const handleSearch = () => {
    performSearch(searchTerm);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const getId = (val) =>
    typeof val === "object" && val ? (val._id || val.id || "") : (val || "");

  // Extract trackId + editId from a row record
  // Extract trackId + editId from a row record
  const extractIds = (record) => {
    // First, log what fields are available
    // console.log("Record fields:", {
    //   _id: record._id,
    //   parentTrackId: record.parentTrackId,
    //   trackId: record.trackId,
    //   editId: record.editId,
    //   edit: record.edit,
    //   versionId: record.versionId
    // });
    
    // Try to get the parent track ID - prioritize parentTrackId
    const trackId = record.parentTrackId;
    // Get the edit ID - this is usually the _id of the edit itself
    const editId = record.editId ||record._id || "";
    
    //console.log("Extracted IDs:", { trackId, editId });
    return { trackId, editId };
  };

  // Check if this row is already assigned
  const isTrackAssigned = (record) => {
    const { trackId, editId } = extractIds(record);
    
    console.log("Checking assignment for:", { trackId, editId });
    console.log("Against excludeIds:", excludeIds);
    
    if (!trackId || !editId) return false;
    
    return Array.isArray(excludeIds) && excludeIds.some((x) => {
      const excludeTrackId = getId(x.trackId);
      const excludeEditId = getId(x.editId);
      
      console.log("Comparing:", { excludeTrackId, excludeEditId }, "with", { trackId, editId });
      
      return excludeTrackId === trackId && excludeEditId === editId;
    });
  };

  const defaultColumns = [
    {
      title: "Track",
      key: "track",
      width: "38%",
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <div className="h-8 rounded flex items-center justify-center">
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.25 3.99981C21.25 3.88964 21.2257 3.78083 21.1788 3.68112C21.132 3.5814 21.0638 3.49323 20.979 3.42288C20.8942 3.35252 20.795 3.3017 20.6883 3.27403C20.5817 3.24637 20.4703 3.24253 20.362 3.26281L8.362 5.51281C8.19005 5.54499 8.03476 5.63628 7.923 5.77086C7.81124 5.90545 7.75005 6.07487 7.75 6.24981V14.4588C7.17258 14.1531 6.52834 13.9954 5.875 13.9998C5.001 13.9998 4.181 14.2698 3.565 14.7398C2.947 15.2098 2.5 15.9138 2.5 16.7498C2.5 17.5858 2.947 18.2898 3.564 18.7598C4.181 19.2298 5.001 19.4998 5.875 19.4998C6.749 19.4998 7.569 19.2298 8.185 18.7598C8.791 18.2998 9.232 17.6138 9.249 16.7998L9.25 16.7708V10.6198L19.75 8.60981V12.2088C19.1726 11.9031 18.5283 11.7454 17.875 11.7498C17.001 11.7498 16.181 12.0198 15.565 12.4898C14.947 12.9598 14.5 13.6638 14.5 14.4998C14.5 15.3358 14.947 16.0398 15.564 16.5098C16.181 16.9798 17.001 17.2498 17.875 17.2498C18.749 17.2498 19.569 16.9798 20.185 16.5098C20.791 16.0498 21.232 15.3638 21.25 14.5498V3.99981ZM19.75 14.4998C19.75 14.7678 19.61 15.0638 19.277 15.3178C18.944 15.5708 18.451 15.7498 17.875 15.7498C17.299 15.7498 16.806 15.5708 16.473 15.3178C16.141 15.0638 16 14.7678 16 14.4998C16 14.2318 16.14 13.9358 16.473 13.6818C16.806 13.4288 17.299 13.2498 17.875 13.2498C18.451 13.2498 18.944 13.4288 19.277 13.6818C19.609 13.9358 19.75 14.2318 19.75 14.4998ZM7.277 15.9318C7.609 16.1858 7.75 16.4818 7.75 16.7498C7.75 17.0178 7.61 17.3138 7.277 17.5678C6.944 17.8208 6.45 17.9998 5.875 17.9998C5.3 17.9998 4.806 17.8208 4.473 17.5678C4.141 17.3138 4 17.0178 4 16.7498C4 16.4818 4.14 16.1858 4.473 15.9318C4.806 15.6788 5.3 15.4998 5.875 15.4998C6.45 15.4998 6.944 15.6788 7.277 15.9318ZM19.75 7.08181L9.25 9.09181V6.87181L19.75 4.90381V7.08181Z"
                fill="#52FAF0"
              />
            </svg>
          </div>
          <div>
            <div>
              <span className="font-semibold text-[14px]">
                {record.trackTitle}{" "}
              </span>
              {record.editTitle && (
                <span className="font-normal text-[14px] text-[#B6B6B6] ml-1">
                  ( {record.editTitle} )
                </span>
              )}
            </div>
            <div
              className="text-xs"
              style={{
                fontSize: "10px",
                fontFamily: "Public Sans",
                color: "#B6B6B6",
                lineHeight: "18px",
              }}
            >
              ID {record.trackId}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Artist(s)",
      key: "artists",
      width: "25%",
      render: (text, record) => (
        <div
          style={{
            fontSize: "14px",
            fontFamily: "Public Sans",
            color: "#E2E2E2",
            lineHeight: "20px",
            fontWeight: "500",
          }}
        >
          {record.artists}
        </div>
      ),
    },
    {
      title: "Version",
      key: "version",
      width: "15%",
      render: (text, record) => (
        <div>
          <div
            style={{
              fontSize: "14px",
              fontFamily: "Public Sans",
              color: "#E2E2E2",
              lineHeight: "20px",
              fontWeight: "500",
            }}
          >
            {record.versionType || "Original"}
          </div>
          <div
            className="text-xs"
            style={{
              fontSize: "12px",
              fontFamily: "Public Sans",
              color: "#B6B6B6",
              lineHeight: "18px",
            }}
          >
            {record.bpm && `${record.bpm} BPM`}
            {record.key && record.bpm && " / "}
            {record.key && `${record.key}`}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "22%",
      render: (text, record) => {
        const isAssigned = isTrackAssigned(record);

        return isAssigned ? (
          <button
            disabled
            className="rounded-[15px] h-[30px] px-3 py-1 text-xs border border-[#A8B0B059] text-[#A8B0B059] font-inter font-[300] cursor-not-allowed flex items-center justify-center gap-1"
          >
            <span>Already Assigned</span>
          </button>
        ) : (
          <CustomButton
            onClick={() => handleSelect(record)}
            className="rounded-[15px] h-[28px] w-[79px] text-xs"
            textColor="text-[#52FAF0]"
          >
            <svg
              className="min-w-[14px] h-[14px]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="ml-1">Select</span>
          </CustomButton>
        );
      },
    },
  ];

  const playlistColumns = [
    {
      title: "Track ID",
      dataIndex: "trackId",
      key: "trackId",
      width: "18%",
      render: (text) => (
        <span
          className="text-[13px] font-medium text-[#E2E2E2] font-PublicSans"
          style={{ lineHeight: "20px" }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Track",
      key: "trackTitle",
      width: "35%",
      render: (_, record) => {
        return (
          <div className="flex flex-col">
            <span className="font-semibold text-[14px] text-[#E2E2E2]">
              {record.trackTitle}
            </span>
            {record.editTitle && (
              <span className="text-[13px] text-[#B6B6B6] font-normal ml-[1px]">
                ({record.editTitle})
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Artist(s)",
      key: "artists",
      width: "27%",
      render: (_, record) => (
        <div
          className="text-[14px] font-medium text-[#E2E2E2] font-PublicSans"
          style={{ lineHeight: "20px" }}
        >
          {record.artists}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "20%",
      render: (_, record) => {
        const isAssigned = isTrackAssigned(record);

        return isAssigned ? (
          <button
            disabled
            className="rounded-[15px] h-[30px] px-3 py-1 text-xs border border-[#A8B0B059] text-[#A8B0B059] font-inter font-[300] cursor-not-allowed flex items-center justify-center gap-1"
          >
            <span>Already Assigned</span>
          </button>
        ) : (
          <CustomButton
            onClick={() => handleSelect(record)}
            className="rounded-[15px] h-[28px] w-[79px] text-xs"
            textColor="text-[#52FAF0]"
          >
            <svg
              className="min-w-[14px] h-[14px]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="ml-1">Select</span>
          </CustomButton>
        );
      },
    },
  ];
  
  const columns = mode === "playlist" ? playlistColumns : defaultColumns;

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#0D1313] border border-[#505050] rounded-lg w-[80%] min-w-[865px] max-h-[80vh] flex flex-col px-[3.25rem]">
        {/* Header */}
        <div className="flex justify-between items-center pb-5 pt-8">
          <h2 className="text-xl font-semibold text-[#FFFFFF] font-public-sans">
            {title}
          </h2>
          <button
            onClick={handleClose}
            className="text-[#C3C3C3] hover:text-white font-sans flex items-center justify-center leading-0 text-[20px] h-5 w-5"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.9998 13.4008L7.0998 18.3008C6.91647 18.4841 6.68314 18.5758 6.3998 18.5758C6.11647 18.5758 5.88314 18.4841 5.6998 18.3008C5.51647 18.1174 5.4248 17.8841 5.4248 17.6008C5.4248 17.3174 5.51647 17.0841 5.6998 16.9008L10.5998 12.0008L5.6998 7.10078C5.51647 6.91745 5.4248 6.68411 5.4248 6.40078C5.4248 6.11745 5.51647 5.88411 5.6998 5.70078C5.88314 5.51745 6.11647 5.42578 6.3998 5.42578C6.68314 5.42578 6.91647 5.51745 7.0998 5.70078L11.9998 10.6008L16.8998 5.70078C17.0831 5.51745 17.3165 5.42578 17.5998 5.42578C17.8831 5.42578 18.1165 5.51745 18.2998 5.70078C18.4831 5.88411 18.5748 6.11745 18.5748 6.40078C18.5748 6.68411 18.4831 6.91745 18.2998 7.10078L13.3998 12.0008L18.2998 16.9008C18.4831 17.0841 18.5748 17.3174 18.5748 17.6008C18.5748 17.8841 18.4831 18.1174 18.2998 18.3008C18.1165 18.4841 17.8831 18.5758 17.5998 18.5758C17.3165 18.5758 17.0831 18.4841 16.8998 18.3008L11.9998 13.4008Z"
                fill="#808080"
              />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="pb-4">
          <div className="flex gap-3 items-center">
            <div className="flex-1">
              <SearchBar
                searchQuery={searchTerm}
                setSearchQuery={handleSearchTermChange}
                setRefreshTrigger={() => {}}
                placeholder="Search by Track Title, Artist, or Track ID"
                containerClass="bg-[#2D2D2D] border border-[#808080] rounded-[5px] px-3 w-full h-[45px]"
                inputClass="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public Sans'] pr-6"
                buttonClass="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
                closeIconClass="w-4 h-4"
                onKeyPress={handleKeyPress}
              />
            </div>
            <CustomButton
              onClick={handleSearch}
              className="h-[45px] text-[11px]"
              disabled={loading}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              SEARCH
            </CustomButton>
          </div>
        </div>

        {/* Table Container */}
        <div
          className="flex-1 overflow-hidden my-0"
          style={{
            borderRadius: "8px",
            border: "1px solid #505050",
            background: "#191919",
            marginTop: "0px",
            minHeight: "40vh",
            maxHeight: "calc(80vh - 250px)",
          }}
        >
          <div style={{ height: "100%", overflow: "auto" }}>
            <Table
              columns={columns}
              dataSource={tracks}
              rowKey={(r) => {
                const { trackId, editId } = extractIds(r);
                const uniqueKey = `${trackId}:${editId}:${r.trackId || ''}`;
                return uniqueKey;
              }}
              pagination={false}
              scroll={{
                y: "calc(80vh - 300px)",
                scrollToFirstRowOnChange: true,
              }}
              rowClassName={(record, index) => {
                return index % 2 === 0 ? "even-row" : "odd-row";
              }}
              style={{
                height: "100%",
                background: "#191919",
              }}
              components={{
                header: {
                  cell: (props) => (
                    <th
                      {...props}
                      style={{
                        backgroundColor: "#0D141B",
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#FFFFFF",
                        letterSpacing: "0.014em",
                        padding: "12px 0px 12px 50px",
                        borderBottom: "1px solid #505050",
                        lineHeight: "22px",
                      }}
                    />
                  ),
                },
                body: {
                  cell: (props) => (
                    <td
                      {...props}
                      style={{
                        fontSize: "12px",
                        fontFamily: "Public Sans",
                        color: "#E2E2E2",
                        padding: "12px 0px 12px 50px",
                        lineHeight: "20px",
                        borderBottom: "1px solid #505050",
                      }}
                    />
                  ),
                },
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-[#505050]">
          <button
            onClick={handleClose}
            className="w-[70px] text-center h-[28px] border text-[#868686] border-[#505050] text-[11px] leading-0 font-public-sans rounded-md hover:bg-[#2D2D2D] transition-colors"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrackChartSelectorModal;